const Slot = require('../domain/Slot'),
	adManager = require('./adManager'),
	lazyLoadService = require('./lazyLoadService'),
	breakpoints = require('./breakpoints'),
	sizeMappings = require('./sizeMappings'),
	slotsStates = require('../domain/SlotStates'),
	arrayUtils = require('../util/array-util');

const DEFAULT_SLOT_LOAD_BATCH_COUNT = 12;

let slots = [],
	loadSlotBatchCount,
	waitingForDefine;

function init(settings) {
	performance.mark('advert - xandr - slotmanager.init - start');

	loadSlotBatchCount = settings.loadSlotBatchCount || DEFAULT_SLOT_LOAD_BATCH_COUNT;
	waitingForDefine = {};

	slots = _createSlots(settings.slots);

	window.advert.cmd.push(['loaded', _defineSlots]);

	_hookEvents();

	performance.mark('advert - xandr - slotmanager.init - end');
}

function _hookEvents() {
	window.advert.pubsub.subscribe('breakpoint.changed', _onBreakpointChange);
	window.advert.pubsub.subscribe('slot.defined', _onSlotDefined);
}

function _onBreakpointChange(currentBreakpoint) {
	return Promise.all(slots
		.filter((/** Slot*/ slot) => slot.state === slotsStates.RENDERED)
		.filter((/** Slot*/ slot) => slot.renderBreakpoint && slot.renderBreakpoint !== currentBreakpoint)
		.map((slot) => slot.refresh('breakpoint_changed')));
}

function _isUsedOnCurrentBreakpoint(slot) {
	const activeBreakpoint = breakpoints.getCurrentBreakpoint(),
		sm = sizeMappings.getSizeMapping(slot.sizeMapping);

	return typeof sm[activeBreakpoint] !== 'undefined' && sm[activeBreakpoint].length > 0;
}

function _createSlots(settingSlots) {
	return settingSlots.map((s) => {
		try {
			return new Slot(s);
		} catch (e) {
			console.error('[ADVERT] Could not create slot. Error:', e);

			return null;
		}
	}).filter(s => s !== null);
}

async function _defineSlots() {
	performance.mark('advert - xandr - slotmanager.define - start');

	const definedSlots = (
		await Promise.all(slots
			.filter((slot) => slot.getPreload())
			.filter(_isUsedOnCurrentBreakpoint)
			.map((slot) => slot.define().catch((e) => {
				console.error('[ADVERT] Could not define slot. Error:', e);

				return null;
			})))
	).filter(s => s !== null);

	performance.mark('advert - xandr - slotmanager.extendSlots - start');
	await window.advert.pubsub.publish('extendSlots', definedSlots);
	performance.mark('advert - xandr - slotmanager.extendSlots - end');

	let batchCount;

	if (typeof loadSlotBatchCount === 'object') {
		batchCount = loadSlotBatchCount[breakpoints.getCurrentBreakpoint()];
	} else {
		batchCount = loadSlotBatchCount;
	}

	const batches = arrayUtils.getBatched(definedSlots, typeof batchCount === 'number' ? batchCount : DEFAULT_SLOT_LOAD_BATCH_COUNT);

	await Promise.all(batches.map(_fetchSlots));

	performance.mark('advert - xandr - slotmanager.define - end');
}

function _lazyLoadSlot(slot) {
	return lazyLoadService.waitForSlot(slot).catch((e) => {
		console.error('[ADVERT] Could not Lazy load slot slot. Error:', e);

		return null;
	});
}

async function _fetchSlots(definedSlots) {
	return (await Promise.all(adManager.loadSlots(definedSlots).map((promise) => {
		return promise.catch((e) => {
			console.error('[ADVERT] Could not load slot. Error:', e);

			return null;
		});
	}))).filter(s => s !== null);
}

function _waitForSlotDefine(slot) {
	return new Promise((resolve) => {
		if (slot.state === slotsStates.DEFINED || slot.state === slotsStates.RENDERED) {
			resolve();

			return;
		}

		waitingForDefine[slot.name] = resolve;
	});
}

function _onSlotDefined(slot) {
	if (typeof waitingForDefine[slot.name] === 'function') {
		waitingForDefine[slot.name]();
		delete waitingForDefine[slot.name];
	}
}

async function loadSlot(slotName, nodeId) {
	const slot = slots.find((s) => s.name === slotName),
		slotIdentifier = nodeId || slotName;

	if (!slot) {
		throw new Error(`No slot found for ${slotName}`);
	}

	// Slot already loaded for this node
	if (slot.node && slot.node.getAttribute('data-advert-orig-id') === slotIdentifier) {
		return;
	}

	const node = document.getElementById(slotIdentifier);

	if (!node) {
		throw new Error(`No DOM element found found for id ${slotIdentifier}`);
	}

	const previousNode = slot.node;

	await slot.updateNode(node);

	await _waitForSlotDefine(slot);

	if (slot.lazyLoad) {
		await _lazyLoadSlot(slot);
	}

	// Trying to load for a different DOM position
	if (previousNode && previousNode !== node && slot.state === slotsStates.RENDERED) {
		return slot.refresh(null); // No reason, it's not a 'real' refresh
	}

	return slot.render();
}

function getSlot(slotName) {
	return slots.find((s) => s.name === slotName);
}

function getSlots() {
	return [...slots];
}

module.exports = {
	init,
	loadSlot,
	getSlot,
	getSlots
};
